/* eslint-disable max-len */
import { Col, Row } from 'antd';
import Modal from 'antd/lib/modal/Modal';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';

import AnchorComponent from 'components/atoms/AnchorComponent';
import ButtonComponent from 'components/atoms/ButtonComponent';
import Paragraph from 'components/atoms/Paragraph';
import TableComponent from 'components/atoms/TableComponent';
import Title from 'components/atoms/Title';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';

import useNavigateWorkaround from 'hooks/useNavigateWorkaround';
import useProfily from 'hooks/useProfily';

import identifikaceEmitenta from 'services/akcionar/emise/identifikaceEmitenta';
import podaneZadostiApi, { IPodaneZadosti, IZadostiTable } from 'services/akcionar/emise/podaneZadosti';
import { ETypZadosti, zruseniZadosti } from 'services/akcionar/emise/zruseniZadosti';
import fileDownload from 'services/fileDownload';
import { vyjadreniCdcpIsinApi } from 'services/spravaEmitenta/vyjadreniCdcp';

import { EZadostStatus } from 'enums/amlz/zadostStatus';
import routePathEnum from 'enums/routePathEnum';

const EmisePodaneZadosti: React.FC = () => {
    const { navigateWorkaround } = useNavigateWorkaround();
    const { setLevel } = useProfily();
    const { aktivniFirma } = useContext(Context);
    const [podaneZadosti, setPodaneZadosti] = useState<IZadostiTable>();
    const [errStatus, setErrStatus] = useState<number>();
    const [loading, setLoading] = useState<boolean>(false);
    const [offset, setOffset] = useState<number>(0);
    const [refetchData, setRefetchData] = useState<boolean>(false);
    const [modalMessage, setModalMessage] = useState<string>('');

    /** Clean up akce */
    useEffect(() => {
        return () => setLoading(false);
    }, []);

    useEffect(() => {
        setLoading(true);
        setErrStatus(0);
        podaneZadostiApi('datumzmeny:desc', aktivniFirma?.ic ? aktivniFirma.ic : '', 'zadostoisin', 10, offset)
            .then(resp => {
                setPodaneZadosti(resp);
            })
            .catch((err: any) => {
                setErrStatus(err.response?.status);
            })
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [offset, refetchData]);

    const columns = [
        {
            title: <TranslateText>EMP_PODANE_ZADOSTI_DATUM_PODANI</TranslateText>,
            key: 'datum',
            dataIndex: 'datum',
            render: (datum: string | null | undefined) => {
                return dayjs(datum).format(' DD.MM.YYYY');
            },
            sorter: (a: { datum: string }, b: { datum: string }) => dayjs(a.datum).unix() - dayjs(b.datum).unix()
        },
        {
            title: <TranslateText>TYP_ZADOSTI</TranslateText>,
            key: 'typzadosti',
            dataIndex: 'typzadosti',
            render: (typzadosti: string, row: IPodaneZadosti) =>
                typzadosti ? (
                    <div>
                        <TranslateText>{`EMISE_PODZAD_${typzadosti.toUpperCase().split(' ').join('_')}`}</TranslateText>
                        {row.druhcp && (
                            <>
                                {' ('}
                                <TranslateText>
                                    {`ISIN_EMISE_DRUH_${row.druhcp.toUpperCase().split(' ').join('_')}`}
                                </TranslateText>
                                {')'}
                            </>
                        )}
                    </div>
                ) : (
                    ''
                ),
            sorter: (a: { datum: string }, b: { datum: string }) => dayjs(a.datum).unix() - dayjs(b.datum).unix()
        },
        {
            title: <TranslateText>EMP_PODANE_ZADOSTI_STAV</TranslateText>,
            key: 'stav',
            dataIndex: 'stav',
            render: (stav: string) =>
                stav ? (
                    <TranslateText>{`EMP_PODANE_ZADOSTI_${stav.toUpperCase().split(' ').join('_')}`}</TranslateText>
                ) : (
                    ''
                ),
            sorter: (a: { stav: string }, b: { stav: string }) => (a.stav && b.stav ? a.stav.localeCompare(b.stav) : 0)
        },
        {
            dataIndex: 'edit',
            key: 'edit',
            width: 200,
            render: (_: IPodaneZadosti, row: IPodaneZadosti, i: number) => (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-evenly',
                        flexGrow: 1
                    }}
                >
                    {row.stav !== EZadostStatus.ZRUSENO && (
                        <AnchorComponent
                            id={`prehledEmiseStahnoutBtn${i}`}
                            onClick={() => {
                                setLoading(true);
                                identifikaceEmitenta(
                                    'get',
                                    row.id,
                                    undefined,
                                    row.typzadosti === ETypZadosti.REGI ? 'Registration' : undefined
                                ).then(data => {
                                    if (data.redirect) {
                                        fileDownload(data.redirect, `${row.id}_ZadostISIN.pdf`).finally(() =>
                                            setLoading(false)
                                        );
                                    } else {
                                        setLevel('isin', {
                                            ...data,
                                            idZadosti: row.id,
                                            stav: row.stav
                                        });
                                        navigateWorkaround(
                                            aktivniFirma
                                                ? row.typzadosti === ETypZadosti.PRID
                                                    ? `/firma/${aktivniFirma?.iducet}${routePathEnum.EMISE_NOVY_ISIN}`
                                                    : row.typzadosti === ETypZadosti.ZMEN
                                                      ? `/firma/${aktivniFirma?.iducet}${routePathEnum.EMISE_ZMENY_ISIN}`
                                                      : row.typzadosti === ETypZadosti.ZRUS
                                                        ? `/firma/${aktivniFirma?.iducet}${routePathEnum.EMISE_ZRUSENI_ISIN}`
                                                        : row.typzadosti === ETypZadosti.REGI
                                                          ? `/firma/${aktivniFirma?.iducet}${routePathEnum.EMISE_REGI_ISIN}`
                                                          : ''
                                                : ''
                                        );
                                    }
                                });
                            }}
                        >
                            <TranslateText>ZOBRAZIT</TranslateText>
                        </AnchorComponent>
                    )}
                    {(row.stav === EZadostStatus.ZAMITNUTO || row.stav === EZadostStatus.KDOPLNENI) && (
                        <AnchorComponent
                            id={`prehledEmiseVyjadreniBtn${i}`}
                            onClick={() => {
                                setLoading(true);
                                vyjadreniCdcpIsinApi(row.id)
                                    .then(data => setModalMessage(data.vyjadreni))
                                    .finally(() => setLoading(false));
                            }}
                        >
                            <TranslateText>VYJADRENI</TranslateText>
                        </AnchorComponent>
                    )}
                    {row.stav !== EZadostStatus.ZRUSENO &&
                        row.stav !== EZadostStatus.AKCEPTOVANO &&
                        row.stav !== EZadostStatus.ODESLANO &&
                        row.stav !== EZadostStatus.ZAMITNUTO && (
                            <AnchorComponent
                                id={`prehledEmiseZrusitBtn${i}`}
                                onClick={() => {
                                    setLoading(true);
                                    row.typzadosti &&
                                        zruseniZadosti(row.id, row.typzadosti).then(() => setRefetchData(val => !val));
                                }}
                            >
                                <TranslateText>ZRUSIT</TranslateText>
                            </AnchorComponent>
                        )}
                </div>
            )
        }
    ];

    return (
        <>
            {errStatus ? (
                <Row
                    justify="center"
                    align="middle"
                    style={{
                        height: '100%'
                    }}
                >
                    <Col span={16} flex={'0 0 auto'}>
                        {errStatus === 404 ? (
                            <Title text="ZADNE_EMISE" level={3} />
                        ) : errStatus ? (
                            <Title text="SLUZBA_NEDOSTUPNA" level={3} />
                        ) : null}
                    </Col>
                </Row>
            ) : (
                <>
                    <Title level={3} text="ZADOSTI_ISIN" />
                    <Row gutter={8}>
                        <Col span={24}>
                            <TableComponent
                                columns={columns}
                                rowKey={record => record.id}
                                dataSource={podaneZadosti?.zadosti}
                                loading={!podaneZadosti || loading}
                                pagination={{
                                    total: podaneZadosti?.count,
                                    onChange: page => setOffset((page - 1) * 10),
                                    current: offset / 10 + 1,
                                    showSizeChanger: false
                                }}
                                locale={{
                                    emptyText: <TranslateText>ZADNE_UDAJE</TranslateText>
                                }}
                            />
                        </Col>
                    </Row>
                </>
            )}
            <Modal
                title={<TranslateText>{'VYJADRENI_CDCP'}</TranslateText>}
                open={!!modalMessage}
                onCancel={() => setModalMessage('')}
                afterClose={() => {
                    setModalMessage('');
                }}
                footer={null}
            >
                <Row>
                    <Paragraph text={modalMessage} />
                </Row>
                <Row className="button-row">
                    <ButtonComponent
                        id="zadostiZavritModalBtn"
                        buttontext="ZAVRIT"
                        onClick={() => {
                            setModalMessage('');
                        }}
                    />
                </Row>
            </Modal>
        </>
    );
};

export default EmisePodaneZadosti;
